import { useEffect } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Skeleton } from "@/components/ui/skeleton"
import { AlertCircle } from "lucide-react"
import { Outlet, useParams } from "react-router-dom"

import { useUserWithOrganization } from "../hooks/use-user-with-organization"
import { useAdminStore } from "../store/admin-store"
import { useRecentUsersStore } from "../store/recent-users-store"
import { AdminBreadcrumbs } from "./admin-breadcrumbs"
import { AdminPageContainer } from "./admin-page-container"
import { OrganizationContextHeader } from "./organization-context-header"
import { UserTabNavigation } from "./user-tab-navigation"

/**
 * Layout para páginas de detalhes do usuário com sistema de abas
 */
export function UserDetailLayout() {
  const { id } = useParams<{ id: string }>()
  const { user, organization, loading, error } = useUserWithOrganization(id!)
  const { setCurrentContext, setBreadcrumbs } = useAdminStore()
  const registerRecentAccess = useRecentUsersStore(state => state.registerAccess)

  // Atualizar contexto no store quando usuário/organização carregarem
  useEffect(() => {
    if (user) {
      setCurrentContext(user, organization)

      registerRecentAccess({
        id: user.id,
        name: user.name,
        email: user.email,
        organizationName: organization?.name,
        lastAccessType: "view",
      })

      // Atualizar breadcrumbs
      setBreadcrumbs([
        { label: "Admin", href: "/admin" },
        { label: "Usuários", href: "/admin/users" },
        { label: user.name, isActive: true },
      ])
    }

    return () => {
      // Limpar contexto quando sair da página
      setCurrentContext(null, null)
      setBreadcrumbs([])
    }
  }, [user, organization, setCurrentContext, setBreadcrumbs, registerRecentAccess])

  if (loading) {
    return (
      <AdminPageContainer
        title="Carregando..."
        subtitle="Obtendo informações do usuário"
        showBackButton
        backTo="/admin/users"
      >
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-12 w-full" />
          <div className="space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
          </div>
        </div>
      </AdminPageContainer>
    )
  }

  if (error || !user) {
    return (
      <AdminPageContainer
        title="Erro"
        subtitle="Não foi possível carregar as informações do usuário"
        showBackButton
        backTo="/admin/users"
      >
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error?.message || "Usuário não encontrado"}</AlertDescription>
        </Alert>
      </AdminPageContainer>
    )
  }

  return (
    <AdminPageContainer
      title={user.name}
      subtitle={organization ? `Organização: ${organization.name}` : "Detalhes do usuário"}
      showBackButton
      backTo="/admin/users"
    >
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <AdminBreadcrumbs
          items={[
            { label: "Admin", href: "/admin" },
            { label: "Usuários", href: "/admin/users" },
            { label: user.name, isActive: true },
          ]}
        />

        {/* Header com contexto */}
        <OrganizationContextHeader user={user} organization={organization} />

        {/* Navegação por abas */}
        <UserTabNavigation />

        {/* Conteúdo da aba ativa */}
        <div className="min-h-96">
          <Outlet />
        </div>
      </div>
    </AdminPageContainer>
  )
}
